import React, {
  createRef,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { isUndefined } from "lodash";
import { RouteComponentProps, useHistory, useLocation } from "react-router";
import { z } from "zod";
import { RESOURCE_NAME } from "common/authorization";
import { OBJECT_TYPE, SUBMISSION_TYPE_MODULE } from "common/constants";
import { buildLink } from "common/routing";
import { SubmissionDefaultValuesContext } from "common/services/formBuilderService/defaults";
import { generateDefaultValuesObject } from "common/utils/defaultValues";
import { isNotNil } from "common/utils/tools";
import { getObjectDisplayForAttached } from "common-client/utils/customObject";
import {
  GetPropertyForSubmissionQuery,
  useCreateSubmissionMutation,
  useGetCustomMapGeometryForSubmissionQuery,
  useGetOrCreateGuestPropertyLazyQuery,
  useGetOrCreatePropertyLazyQuery,
  useGetPropertyForSubmissionLazyQuery,
  useGetPropertyForSubmissionQuery,
  useGetSisdRuleDataQuery,
  useGetSubmissionQuery,
  useGetSubmissionTypeQuery,
} from "../../generated/graphql";
import { useStatusToasts } from "../../hooks/useStatusToasts";
import useURLParsing from "../../hooks/useURLParsing";
import { setTitle } from "../../utils/title";
import { track } from "../../utils/tracking";
import { AuthContext } from "../Authorization/AuthContext";
import { Markdown } from "../Common/__styles__/Markdown";
import { Button } from "../Common/Button";
import FullPageFormLayout from "../Common/FullPageFormLayout";
import AccessDeniedPage, {
  ACCESS_DENIED_RESOURCE,
} from "../Guest/AccessDeniedPage";
import { accountTitle as getAccountTitle } from "../Guest/utils";
import { SearchResultProps } from "../Search";
import { ButtonContainer } from "./__styles__/Submission";
import useSubmissionsChatAgent, {
  createFormUpdateHandler,
} from "./ChatAgent/SubmissionsChatAgent";
import { FormComponent, FormDataType } from "./Form";
import SubmissionHeader from "./SubmissionHeader";
import SubmissionSuccess from "./SubmissionSuccess";
import {
  canCreateSubmissionTypeFromAuthInfo,
  generateUpsertErrorMessage,
  submissionTypeContainsEcDocumentType,
} from "./utils";

interface CreateSubmissionParams {
  objectId?: string;
  objectType?: OBJECT_TYPE;
  submissionTypeId: string;
  taskId?: string;
}

export interface CreateSubmissionProps
  extends RouteComponentProps<CreateSubmissionParams> {}

type HistoryLocationState = {
  prevLocation?: string;
  search?: string;
  formData?: any;
  relatedSubmissionId?: string;
  isGlobal?: boolean;
  workflowStepId?: string;
};

export const CreateSubmission = ({
  objectId,
  objectType,
  submissionTypeId,
  taskId,
}: CreateSubmissionParams) => {
  const history = useHistory();
  const location = useLocation<Maybe<HistoryLocationState>>();
  const relatedSubmissionId = location.state?.relatedSubmissionId ?? null;
  const workflowStepId = location.state?.workflowStepId ?? null;
  const isGlobal = location.state?.isGlobal ?? false;
  const [disabled, setDisabled] = useState(false);
  const initialFormData = location.state?.formData;
  const [serverProvidedFormData, setServerProvidedFormData] =
    useState<FormDataType | null>(null);

  const { addErrorToast, addSuccessToast } = useStatusToasts();
  const [showSubmissionSuccess, setShowSubmissionSuccess] = useState(false);
  const accountTitle = getAccountTitle();
  const { user, admin, isPublic, account, publicUser, authorized } =
    useContext(AuthContext);

  // property is undefined when it has never been set. If there is no property, it will be set to null
  const [property, setProperty] =
    useState<Maybe<GetPropertyForSubmissionQuery["property"]>>(undefined);

  // Set defaultValues to null, until we have a non-undefined property
  const [defaultValues, setDefaultValues] =
    useState<Maybe<SubmissionDefaultValuesContext>>(null);
  const isMountedRef = useRef(true);

  useLayoutEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const { data, loading, error } = useGetSubmissionTypeQuery({
    fetchPolicy: "cache-and-network",
    variables: { submissionTypeId },
  });

  const { loading: propertyLoading } = useGetPropertyForSubmissionQuery({
    variables: { propertyId: objectId! },
    skip: !objectId || objectType !== OBJECT_TYPE.PROPERTY,
    onCompleted: data => setProperty(data?.property ?? null),
  });

  const [getPropertyForSubmission] = useGetPropertyForSubmissionLazyQuery({
    onCompleted: data => {
      setProperty(data?.property ?? null);
    },
  });

  const { data: customMapGeometryData, loading: customMapGeometryLoading } =
    useGetCustomMapGeometryForSubmissionQuery({
      variables: { geometryId: objectId! },
      skip: !objectId || objectType !== OBJECT_TYPE.CUSTOM_MAP_GEOMETRY,
    });

  const { data: relatedSubmissionData, loading: relatedSubmissionLoading } =
    useGetSubmissionQuery({
      variables: { submissionId: relatedSubmissionId!, isPublic },
      skip: !relatedSubmissionId,
    });

  const submissionTypeModules = account?.submissionTypes.find(
    submissionType => submissionType.id === submissionTypeId,
  )?.modules;

  const { data: sisdRuleData, loading: sisdRuleDataLoading } =
    useGetSisdRuleDataQuery({
      variables: { propertyId: objectId! },
      skip:
        !submissionTypeModules?.includes(
          SUBMISSION_TYPE_MODULE.SUBSTANTIAL_IMPROVEMENT_SUBSTANTIAL_DAMAGE,
        ) ||
        !objectId ||
        objectType !== OBJECT_TYPE.PROPERTY,
    });

  const submissionType = data?.submissionType;
  const [createSubmission, { loading: createLoading }] =
    useCreateSubmissionMutation({
      onCompleted: data => {
        const submission = data.createSubmission;
        const workflowId = submission.workflowStep?.workflow.id;
        track("Submission created", {
          submissionId: submission.id,
          workflowId,
          "Submission Category": submissionType?.category,
          "Submission Type": submissionType?.name,
          "is global": isGlobal,
        });

        if (isPublic) {
          if (workflowId) {
            if (submissionType?.successMessage) {
              addSuccessToast(submissionType.successMessage);
            }

            history.push({
              pathname: buildLink("workflow", {
                workflowId,
              }),
            });
          } else {
            setShowSubmissionSuccess(true);
          }
        } else {
          if (data.createSubmission.summaries?.length) {
            history.push({
              pathname: buildLink("submissionSummary", {
                submissionId: submission.id,
              }),
              state: { prevLocation },
            });
          } else {
            history.push({
              pathname: buildLink("editSubmission", {
                submissionId: submission.id,
              }),
              state: { prevLocation },
            });
          }
          if (submissionType?.successMessage) {
            addSuccessToast(submissionType.successMessage);
          }
        }
      },
      onError: error => {
        const message = generateUpsertErrorMessage(error);
        addErrorToast(message);
      },
    });

  const [getOrCreateProperty, { loading: propertyFromSearchLoading }] =
    useGetOrCreatePropertyLazyQuery();
  const [
    getOrCreateGuestProperty,
    { loading: guestPropertyFromSearchLoading },
  ] = useGetOrCreateGuestPropertyLazyQuery();

  const handleSearchResultClick = async (result: SearchResultProps) => {
    if (result.propertyId) {
      const { data } = await getPropertyForSubmission({
        variables: { propertyId: result.propertyId },
      });
      setProperty(data?.property || null);
    } else {
      const { data: createPropertyData } = await (
        isPublic ?
          getOrCreateGuestProperty
        : getOrCreateProperty)({
        variables: {
          address: result.address,
          latitude: result.point.latitude,
          longitude: result.point.longitude,
          parcelId: result.geocacheParcelId,
          improveParcelCoordinates: true,
        },
        onError: () => {
          addErrorToast(
            "There was an issue finding a property with this address. Please provide a different address",
          );
        },
      });

      if (!createPropertyData?.getProperty) {
        addErrorToast(
          "There was an issue finding a property with this address. Please provide a different address",
        );
      } else {
        const { data } = await getPropertyForSubmission({
          variables: {
            propertyId: createPropertyData?.getProperty?.id,
          },
        });
        setProperty(data?.property || null);
      }
    }
  };

  useEffect(() => {
    if (!isMountedRef.current) return;

    const defaultValues = generateDefaultValuesObject({
      property,
      user: user ?? admin,
    });
    // Only set defaultValues if we have a property. This prevents us from showing the form while defaults are loading
    if (
      !isUndefined(property) ||
      !objectId ||
      objectType !== OBJECT_TYPE.PROPERTY
    ) {
      setDefaultValues(defaultValues);
    }
  }, [property, user, admin, objectId, objectType]);

  const handleFormUpdate = useMemo(
    () =>
      createFormUpdateHandler({
        setServerProvidedFormData,
        setDisabled,
        addSuccessToast,
        isMountedRef,
      }),
    [setDisabled, addSuccessToast, setServerProvidedFormData, isMountedRef],
  );

  const chatAgentConfig = useMemo(
    () => ({
      isPublic,
      submissionTypeVersionId: data?.submissionType?.currentVersion?.id,
      formData: initialFormData ?? {},
      onFormUpdate: handleFormUpdate,
    }),
    [
      isPublic,
      data?.submissionType?.currentVersion?.id,
      initialFormData,
      handleFormUpdate,
    ],
  );

  const {
    ChatToggleButton,
    ChatPanel: SidePanel,
    isChatPanelOpen: isSidePanelOpen,
  } = useSubmissionsChatAgent(chatAgentConfig);

  if (
    loading ||
    propertyLoading ||
    customMapGeometryLoading ||
    relatedSubmissionLoading ||
    sisdRuleDataLoading ||
    error ||
    !data?.submissionType ||
    !defaultValues
  ) {
    return <div />;
  }

  const canCreateSubmissionType = canCreateSubmissionTypeFromAuthInfo({
    submissionType: data?.submissionType,
    user,
    admin,
    publicUser,
  });

  if (!canCreateSubmissionType) {
    return <AccessDeniedPage resource={ACCESS_DENIED_RESOURCE.RECORD} />;
  }

  if (isPublic) {
    setTitle(`Create Record | ${accountTitle}`);
  }

  const submitFormRef = createRef<HTMLButtonElement>();
  const versionId = data.submissionType.currentVersion.id;

  const baseLocation = isPublic ? "guestHome" : "map";

  const prevLocation = `${
    location.state?.prevLocation ?? buildLink(baseLocation)
  }${location.state?.search ?? ""}`;

  const handleSubmit = async (formData: FormDataType) => {
    const canViewWorkflow = authorized({
      resource: RESOURCE_NAME.WORKFLOW,
      permission: "view",
    });

    await createSubmission({
      variables: {
        data: {
          formData,
          versionId,
          relatedSubmissionId,
          ...(property || (objectId && objectType) ?
            {
              objectId: property?.id || objectId,
              objectType: objectType || OBJECT_TYPE.PROPERTY,
            }
          : {}),
          taskId,
          workflowStepId,
        },
        isPublic,
        canViewWorkflow,
      },
    });
  };

  const handleOnChange = ({
    documentUploadsInProgress = false,
    searchAddressCleared = false,
  }: {
    documentUploadsInProgress?: boolean;
    searchAddressCleared?: boolean;
  }) => {
    if (!isMountedRef.current) return;
    if (searchAddressCleared) {
      setProperty(null);
    } else {
      setDisabled(documentUploadsInProgress);
    }
  };

  const customMapGeometry = customMapGeometryData?.customMapGeometry;
  const attachToProperty =
    objectType === OBJECT_TYPE.PROPERTY &&
    !submissionTypeContainsEcDocumentType({
      submissionType: data.submissionType,
      accountDocumentTypes: account?.accountDocumentTypes,
    });

  const submitButtonDisabled =
    createLoading || disabled || (attachToProperty && !property);

  const rightContainer =
    !isPublic ?
      <ButtonContainer>
        {!isPublic && (
          <>
            <Button
              size="small"
              styleVariant="primary"
              onClick={() => {
                submitFormRef.current!.click();
              }}
              disabled={submitButtonDisabled}
              loading={createLoading}
            >
              Submit
            </Button>
            {ChatToggleButton}
          </>
        )}
      </ButtonContainer>
    : undefined;

  let componentToDisplay: JSX.Element;
  if (showSubmissionSuccess) {
    componentToDisplay = (
      <SubmissionSuccess
        preamble={data.submissionType.preamble!}
        postamble={data.submissionType.postamble!}
      />
    );
  } else {
    componentToDisplay = (
      <FullPageFormLayout
        subtitle={`Add new ${data.submissionType.name}`}
        prevLocation={prevLocation}
        rightContainer={rightContainer}
        isSidePanelOpen={isSidePanelOpen}
        sidePanel={SidePanel}
        onSave={() => {
          submitFormRef.current!.click();
        }}
        buttonText="Submit"
        buttonDisabled={submitButtonDisabled}
        buttonLoading={createLoading}
      >
        <div>
          <SubmissionHeader
            account={{
              logoUrl: account?.logoUrl ?? "",
              name: account?.name ?? "",
            }}
            submissionType={data.submissionType}
            objectDisplay={getObjectDisplayForAttached({
              objectType,
              property,
              customMapGeometry,
            })}
            hiddenFromPublic={data.submissionType.hiddenFromPublicByDefault}
          >
            {data.submissionType.description && (
              <Markdown markdown={data.submissionType.description} />
            )}
          </SubmissionHeader>
          <FormComponent
            submissionType={data.submissionType}
            onSubmit={handleSubmit}
            onChange={handleOnChange}
            submitFormRef={submitFormRef}
            initialFormData={initialFormData}
            relatedSubmissions={[relatedSubmissionData?.submission].filter(
              isNotNil,
            )}
            dynamicDefaultValues={defaultValues}
            account={sisdRuleData?.account}
            property={{
              FIRMInfo: sisdRuleData?.FIRMInfo,
              fullAddress: property?.fullAddress || undefined,
            }}
            loadingFormPaths={{}}
            attachToProperty={attachToProperty}
            onSearchResultClick={handleSearchResultClick}
            loadingSearchValue={
              propertyFromSearchLoading || guestPropertyFromSearchLoading
            }
            serverProvidedFormData={serverProvidedFormData ?? undefined}
          />
        </div>
      </FullPageFormLayout>
    );
  }

  return componentToDisplay;
};

const objectTypeQueryParam = z
  .object({
    objectType: z.enum(OBJECT_TYPE).optional(),
    taskId: z.string().optional(),
  })
  .strict();

const CreateSubmissionContainer = ({ match }: CreateSubmissionProps) => {
  const { submissionTypeId, objectId } = match.params;
  const queryParams = useURLParsing({
    routeName: "createSubmission",
    parser: objectTypeQueryParam,
  });
  const objectType = queryParams?.objectType ?? OBJECT_TYPE.PROPERTY;
  const taskId = queryParams?.taskId;

  return (
    <CreateSubmission
      key={submissionTypeId}
      submissionTypeId={submissionTypeId}
      objectId={objectId}
      objectType={objectType}
      taskId={taskId}
    />
  );
};

export default CreateSubmissionContainer;
